import React from "react";
import * as DialogPrimitive from "@radix-ui/react-dialog";
import { X } from "lucide-react";

// Root component props
interface DialogRootProps {
  open?: boolean;
  defaultOpen?: boolean;
  onOpenChange?: (open: boolean) => void;
  children: React.ReactNode;
}

// Trigger component props
interface DialogTriggerProps {
  className?: string;
  children: React.ReactNode;
  asChild?: boolean;
}

// Content component props
interface DialogContentProps {
  className?: string;
  children: React.ReactNode;
  showCloseButton?: boolean;
}

// Overlay component props
interface DialogOverlayProps {
  className?: string;
}

// Title component props
interface DialogTitleProps {
  className?: string;
  children: React.ReactNode;
}

// Description component props
interface DialogDescriptionProps {
  className?: string;
  children: React.ReactNode;
}

// Close component props
interface DialogCloseProps {
  className?: string;
  children?: React.ReactNode;
}

/**
 * Dialog component wrapping Radix UI Dialog primitive.
 * Used for modal dialogs throughout the app.
 *
 * Features:
 * - Dark theme styling matching app design
 * - Backdrop blur effect
 * - Responsive sizing
 * - Keyboard navigation and focus management
 * - Optional close button
 */
const DialogRoot = ({ open, defaultOpen, onOpenChange, children }: DialogRootProps) => {
  return (
    <DialogPrimitive.Root open={open} defaultOpen={defaultOpen} onOpenChange={onOpenChange}>
      {children}
    </DialogPrimitive.Root>
  );
};

DialogRoot.displayName = "Dialog";

const DialogTrigger = React.forwardRef<HTMLButtonElement, DialogTriggerProps>(
  ({ className, children, asChild }, forwardedRef) => {
    return (
      <DialogPrimitive.Trigger ref={forwardedRef} className={className || ""} asChild={asChild}>
        {children}
      </DialogPrimitive.Trigger>
    );
  }
);

DialogTrigger.displayName = "DialogTrigger";

const DialogOverlay = React.forwardRef<HTMLDivElement, DialogOverlayProps>(
  ({ className }, forwardedRef) => {
    return (
      <DialogPrimitive.Overlay
        ref={forwardedRef}
        className={`fixed inset-0 z-50 bg-black/80 backdrop-blur-sm data-[state=open]:animate-in data-[state=closed]:animate-out data-[state=closed]:fade-out-0 data-[state=open]:fade-in-0 ${
          className || ""
        }`}
      />
    );
  }
);

DialogOverlay.displayName = "DialogOverlay";

const DialogContent = React.forwardRef<HTMLDivElement, DialogContentProps>(
  ({ className, children, showCloseButton = true }, forwardedRef) => {
    return (
      <DialogPrimitive.Portal>
        <DialogOverlay />
        <DialogPrimitive.Content
          ref={forwardedRef}
          className={`fixed left-1/2 top-4 sm:top-1/2 z-50 -translate-x-1/2 -translate-y-0 sm:-translate-y-1/2 bg-gray-900 border-2 border-purple-700 rounded-lg max-w-2xl w-[calc(100%-1rem)] sm:w-full max-h-[90vh] flex flex-col shadow-2xl data-[state=open]:animate-in data-[state=closed]:animate-out data-[state=closed]:fade-out-0 data-[state=open]:fade-in-0 data-[state=closed]:zoom-out-95 data-[state=open]:zoom-in-95 data-[state=closed]:slide-out-to-left-1/2 data-[state=closed]:slide-out-to-top-[1rem] sm:data-[state=closed]:slide-out-to-top-[48%] data-[state=open]:slide-in-from-left-1/2 data-[state=open]:slide-in-from-top-[1rem] sm:data-[state=open]:slide-in-from-top-[48%] ${
            className || ""
          }`}
        >
          {children}
          {showCloseButton && (
            <DialogPrimitive.Close className="absolute top-3 right-3 sm:top-4 sm:right-4 text-gray-400 hover:text-gray-200 transition-colors focus:outline-none focus:ring-2 focus:ring-purple-500 rounded-sm p-1">
              <X size={20} className="sm:w-6 sm:h-6" />
              <span className="sr-only">Close</span>
            </DialogPrimitive.Close>
          )}
        </DialogPrimitive.Content>
      </DialogPrimitive.Portal>
    );
  }
);

DialogContent.displayName = "DialogContent";

const DialogTitle = React.forwardRef<HTMLHeadingElement, DialogTitleProps>(
  ({ className, children }, forwardedRef) => {
    return (
      <DialogPrimitive.Title
        ref={forwardedRef}
        className={`text-2xl font-bold text-purple-400 ${className || ""}`}
      >
        {children}
      </DialogPrimitive.Title>
    );
  }
);

DialogTitle.displayName = "DialogTitle";

const DialogDescription = React.forwardRef<HTMLParagraphElement, DialogDescriptionProps>(
  ({ className, children }, forwardedRef) => {
    return (
      <DialogPrimitive.Description
        ref={forwardedRef}
        className={`text-gray-400 text-sm ${className || ""}`}
      >
        {children}
      </DialogPrimitive.Description>
    );
  }
);

DialogDescription.displayName = "DialogDescription";

const DialogClose = React.forwardRef<HTMLButtonElement, DialogCloseProps>(
  ({ className, children }, forwardedRef) => {
    return (
      <DialogPrimitive.Close ref={forwardedRef} className={className || ""}>
        {children}
      </DialogPrimitive.Close>
    );
  }
);

DialogClose.displayName = "DialogClose";

// Compound component export
export const Dialog = Object.assign(DialogRoot, {
  Trigger: DialogTrigger,
  Content: DialogContent,
  Title: DialogTitle,
  Description: DialogDescription,
  Close: DialogClose,
  Overlay: DialogOverlay,
});

export default Dialog;
